import React, { useRef, useState } from "react";

const wrapIndex = (index, length) => (index + length) % length;

const PerspectiveVideoCarousel = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);
  const activeItem = items[activeIndex];

  const selectSlide = (index) => {
    if (index === activeIndex) return;
    videoRefs.current.forEach((video) => video?.pause());
    setActiveIndex(index);
  };

  const move = (direction) => {
    selectSlide(wrapIndex(activeIndex + direction, items.length));
  };

  const handleCarouselKeyDown = (event) => {
    if (event.target.tagName === "VIDEO") return;
    if (event.key === "ArrowLeft") { event.preventDefault(); move(-1); }
    if (event.key === "ArrowRight") { event.preventDefault(); move(1); }
  };

  const handleCardKeyDown = (event, index) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectSlide(index);
    }
  };

  return (
    <div className="pv_carousel" role="region" aria-roledescription="carousel" aria-label="AI video projects" tabIndex={0} onKeyDown={handleCarouselKeyDown}>
      <div className="pv_stage">
        {items.map((item, index) => {
          let distance = index - activeIndex;
          if (distance > items.length / 2) distance -= items.length;
          if (distance < -items.length / 2) distance += items.length;
          const isActive = index === activeIndex;
          return (
            <div
              className={`pv_slide${isActive ? " is-active" : ""}`}
              key={item.title}
              style={{
                "--slide-offset": `${distance * 62}%`,
                "--slide-rotation": `${distance * -48}deg`,
                "--slide-depth": `${isActive ? 0 : distance === 2 || distance === -2 ? -340 : -100}px`,
                "--slide-scale": isActive ? 1 : distance === 2 || distance === -2 ? 0.7 : 0.82,
              }}
              role={isActive ? undefined : "button"}
              tabIndex={isActive ? undefined : 0}
              aria-label={isActive ? undefined : `Select ${item.title}`}
              onClick={isActive ? undefined : () => selectSlide(index)}
              onKeyDown={isActive ? undefined : (event) => handleCardKeyDown(event, index)}
            >
              <video
                ref={(element) => { videoRefs.current[index] = element; }}
                muted={!isActive}
                controls={isActive}
                playsInline
                preload="metadata"
                tabIndex={isActive ? 0 : -1}
                aria-label={isActive ? item.title : undefined}
                aria-hidden={isActive ? undefined : "true"}
              >
                <source src={item.videoUrl} type="video/mp4" />
                Your browser does not support HTML video.
              </video>
            </div>
          );
        })}
      </div>
      <div className="pv_caption" aria-live="polite" aria-atomic="true">
        <span className="pv_count">{String(activeIndex + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}</span>
        <h3>{activeItem.title}</h3>
      </div>
      <div className="pv_controls" aria-label="Carousel controls">
        <button type="button" className="pv_arrow" aria-label="Previous video" onClick={() => move(-1)}>←</button>
        <div className="pv_dots" aria-label="Choose a video">
          {items.map((item, index) => (
            <button
              type="button"
              key={item.title}
              className={`pv_dot${index === activeIndex ? " is-active" : ""}`}
              aria-label={`Show ${item.title}`}
              aria-current={index === activeIndex ? "true" : undefined}
              onClick={() => selectSlide(index)}
            />
          ))}
        </div>
        <button type="button" className="pv_arrow" aria-label="Next video" onClick={() => move(1)}>→</button>
      </div>
    </div>
  );
};

export default PerspectiveVideoCarousel;
